import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppStore } from '../stores/app-store';
import { Target, Calendar, TrendingUp } from 'lucide-react';
import type { WritingGoal } from '../types';
import { cn } from '../lib/utils';
import {
  ResponsiveModal,
  ResponsiveModalContent,
  ResponsiveModalHeader,
  ResponsiveModalTitle,
  ResponsiveModalFooter,
} from './ui/responsive-modal';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Progress } from './ui/progress';

interface WritingGoalModalProps {
  projectId: string;
  currentGoal?: WritingGoal;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function WritingGoalModal({ projectId, currentGoal, open, onOpenChange }: WritingGoalModalProps) {
  const { t } = useTranslation();
  const setWritingGoal = useAppStore((s) => s.setWritingGoal);
  const getTotalWords = useAppStore((s) => s.getTotalWords);

  const totalWords = getTotalWords(projectId);

  const [goalType, setGoalType] = useState<WritingGoal['type']>(currentGoal?.type || 'daily');
  const [targetWords, setTargetWords] = useState(currentGoal?.targetWords || 1000);
  const [deadline, setDeadline] = useState<string>(
    currentGoal?.deadline ? new Date(currentGoal.deadline).toISOString().split('T')[0] : ''
  );

  const handleSave = () => {
    const goal: WritingGoal = {
      type: goalType,
      targetWords,
      currentWords: goalType === 'project' ? totalWords : currentGoal?.currentWords || 0,
      deadline: deadline ? new Date(deadline) : undefined,
    };
    setWritingGoal(projectId, goal);
    // cerimônia: a flecha de brasa crava a meta no alvo
    window.dispatchEvent(new CustomEvent('reformu:moment', { detail: { type: 'goalset', words: targetWords } }));
    onOpenChange(false);
  };

  const presets = {
    daily: [500, 1000, 1500, 2000],
    project: [10000, 25000, 50000, 80000],
    session: [250, 500, 750, 1000],
  };

  const goalTypeInfo = {
    daily: {
      icon: Calendar,
      title: t('goals.daily', 'Daily Goal'),
      description: t('goals.dailyDesc', 'Words to write each day'),
    },
    project: {
      icon: Target,
      title: t('goals.project', 'Project Goal'),
      description: t('goals.projectDesc', 'Total words for this book'),
    },
    session: {
      icon: TrendingUp,
      title: t('goals.session', 'Session Goal'),
      description: t('goals.sessionDesc', 'Words per writing session'),
    },
  };

  const calculateProgress = () => {
    if (goalType === 'project') {
      return Math.min(100, Math.round((totalWords / targetWords) * 100));
    }
    if (currentGoal) {
      return Math.min(100, Math.round((currentGoal.currentWords / targetWords) * 100));
    }
    return 0;
  };

  const progress = calculateProgress();

  return (
    <ResponsiveModal open={open} onOpenChange={onOpenChange}>
      <ResponsiveModalContent className="sm:max-w-md">
        <ResponsiveModalHeader>
          <ResponsiveModalTitle className="flex items-center gap-2">
            <Target size={20} className="text-primary" />
            {t('goals.writingGoal', 'Writing Goal')}
          </ResponsiveModalTitle>
        </ResponsiveModalHeader>

        <div className="space-y-6">
          {/* Goal Type Selection */}
          <div className="grid grid-cols-3 gap-2">
            {(Object.keys(goalTypeInfo) as Array<keyof typeof goalTypeInfo>).map((type) => {
              const info = goalTypeInfo[type];
              const Icon = info.icon;
              const active = goalType === type;
              return (
                <button
                  key={type}
                  onClick={() => setGoalType(type)}
                  className={cn(
                    'flex flex-col items-center gap-2 rounded-xl border-2 p-3 transition-all',
                    active ? 'border-primary bg-primary/10' : 'border-border hover:border-foreground/30'
                  )}
                >
                  <Icon size={24} className={active ? 'text-primary' : 'text-muted-foreground'} />
                  <span className={cn('text-xs font-medium', active ? 'text-primary' : 'text-muted-foreground')}>
                    {info.title}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Goal Description */}
          <p className="text-center text-sm text-muted-foreground">
            {goalTypeInfo[goalType].description}
          </p>

          {/* Target Words Input */}
          <div className="space-y-2">
            <Label htmlFor="target-words">{t('goals.targetWords', 'Target Words')}</Label>
            <Input
              id="target-words"
              type="number"
              value={targetWords}
              onChange={(e) => setTargetWords(Math.max(0, parseInt(e.target.value) || 0))}
              className="h-auto py-3 text-center text-2xl font-bold"
              min="1"
            />
          </div>

          {/* Quick Presets */}
          <div className="flex flex-wrap justify-center gap-2">
            {presets[goalType].map((preset) => {
              const active = targetWords === preset;
              return (
                <button
                  key={preset}
                  onClick={() => setTargetWords(preset)}
                  className={cn(
                    'rounded-full px-3 py-1.5 text-sm font-medium transition-colors',
                    active ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground hover:bg-muted/70'
                  )}
                >
                  {preset.toLocaleString()}
                </button>
              );
            })}
          </div>

          {/* Deadline (for project goals) */}
          {goalType === 'project' && (
            <div className="space-y-2">
              <Label htmlFor="deadline">
                {t('goals.deadline', 'Deadline')}{' '}
                <span className="font-normal text-muted-foreground">({t('common.optional', 'optional')})</span>
              </Label>
              <Input
                id="deadline"
                type="date"
                value={deadline}
                onChange={(e) => setDeadline(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
              />
              {deadline && (
                <p className="text-xs text-muted-foreground">
                  {t('goals.daysLeft', {
                    days: Math.ceil((new Date(deadline).getTime() - Date.now()) / (1000 * 60 * 60 * 24)),
                    defaultValue: '{{days}} days until deadline',
                  })}
                </p>
              )}
            </div>
          )}

          {/* Progress Preview */}
          {(currentGoal || goalType === 'project') && (
            <div className="rounded-xl bg-muted p-4">
              <div className="mb-2 flex justify-between text-sm">
                <span className="text-muted-foreground">{t('goals.progress', 'Progress')}</span>
                <span className="font-medium text-foreground">{progress}%</span>
              </div>
              <Progress value={progress} />
              <p className="mt-2 text-center text-xs text-muted-foreground">
                {goalType === 'project'
                  ? t('goals.wordsWritten', '{{current}} of {{target}} words', {
                      current: totalWords.toLocaleString(),
                      target: targetWords.toLocaleString(),
                    })
                  : t('goals.todayProgress', '{{current}} words today', {
                      current: (currentGoal?.currentWords || 0).toLocaleString(),
                    })}
              </p>
            </div>
          )}
        </div>

        <ResponsiveModalFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSave}>{t('goals.setGoal', 'Set Goal')}</Button>
        </ResponsiveModalFooter>
      </ResponsiveModalContent>
    </ResponsiveModal>
  );
}
